/**
 * PlayerDotsStrip — the collapsed, tap-to-expand player indicator that lives
 * in the in-game top bar. Replaces the older avatar-chip PlayerRosterStrip.
 *
 * Design (per product):
 *   • One small dot per player, coloured by the player's board colour, packed
 *     tight so the whole control stays narrow.
 *   • A soft green glow marks a live (online) HUMAN — bots never glow.
 *   • The on-turn player gets a yellow ring + a small yellow triangle above,
 *     matching the AvatarChip "on turn" treatment.
 *   • When bootable, the on-turn triangle becomes a red ! notification badge.
 *   • Eliminated players dim to 35%.
 *   • Team games: each dot is drawn in its team's shape (circle, rounded
 *     square, diamond, hexagon; docs/team-games/team-final-four.html). Colours
 *     stay per player and turn order stays the display order. There is no
 *     legend; the drawer names each team beside its shape.
 *   • The entire strip is a single button (bordered pill) that opens the
 *     PlayerRosterPanel details drawer, so it reads as tappable.
 *
 * The who's-live / whose-turn / eliminated rules are factored into the pure
 * `playerDotState` helper (lib/playerDots) so they can be unit-tested.
 */

import React from 'react'
import { CountBubble } from '../ui/CountBubble'
import { View, TouchableOpacity } from 'react-native'
import { PLAYER_COLORS } from '@warcarp/ui'
import { playerColorSlot } from '../../lib/playerColor'
import { useTheme } from '../../hooks/useTheme'
import { usePlayerInfo } from './PlayerRosterPanel'
import {
  playerDotState,
  playerDotSize,
  playerDotsPillWidth,
  playerDotFontSize,
  TEAM_DOT_GAP,
} from '../../lib/playerDots'
import { PlayerDisc } from './PlayerDisc'
import type { GameState } from '../../lib/gameState'
import { teamLabel, teamOf, teamShape, type GamePlayerResponse } from '@warcarp/api-types'

const DOT_GAP = 4
/** How far a live player's glow extends past the ring. */
const GLOW_BLEED = 5
export function PlayerDotsStrip({
  state,
  players,
  presence,
  onOpen,
  active,
  unreadTotal = 0,
  alignment = 'right',
  overdueUserId,
}: {
  state: GameState
  players: GamePlayerResponse[]
  /** Set of userIds currently live (≥1 open socket). */
  presence?: Set<string>
  /** Toggles the player-details drawer (open when closed, dismiss when open). */
  onOpen: () => void
  /** True while the details drawer is open — the pill shows a toggled state. */
  active?: boolean
  /** Total unread secret messages — drives a badge on the pill so unread DMs
   *  are visible without opening the drawer. */
  alignment?: 'center' | 'right'
  unreadTotal?: number
  overdueUserId?: string
}) {
  const playerInfo = usePlayerInfo(state, players)
  const overdueName = playerInfo.find(info => info.player.userId === overdueUserId)?.player.displayName
  const { theme } = useTheme()
  const amber = theme === 'dark' ? '#fbbf24' : '#b45309'
  const green = theme === 'dark' ? '#22c55e' : '#15803d'
  const presenceSet = presence ?? new Set<string>()

  // Measured width of the flex slot this strip occupies. Dots shrink to fit it
  // so the last dot is never clipped in a narrow (landscape) action column.
  const [slotWidth, setSlotWidth] = React.useState(0)
  const n = playerInfo.length
  // Team games space the dots a little wider (the diamond and hexagon overshoot).
  const teamGame = playerInfo.some((info) => teamOf(state, String(info.player.slot)) !== null)
  // Two through six retain the regular size; seven through nine use the
  // former six-player diameter. Measured room can shrink either further.
  const gap = teamGame ? TEAM_DOT_GAP : DOT_GAP
  const dotSize = playerDotSize(slotWidth, n, gap)
  const onTurnCount = playerInfo.filter((info) => info.isTurn && !info.eliminated).length
  const pillWidth = playerDotsPillWidth(dotSize, n, onTurnCount, gap)

  const liveCount = playerInfo.filter(
    (info) =>
      playerDotState({
        isBot: info.player.isBot,
        userId: info.player.userId,
        isTurn: info.isTurn,
        eliminated: info.eliminated,
        presence: presenceSet,
      }).glow,
  ).length

  return (
    <View
      // Fills the space left of the row after the icons; the fixed-width pill
      // is right-aligned inside it. onLayout drives the responsive dot size.
      style={{ flex: 1, minWidth: 0, alignItems: alignment === 'center' ? 'center' : 'flex-end' }}
      pointerEvents="box-none"
      onLayout={(e) => setSlotWidth(e.nativeEvent.layout.width)}
    >
      <TouchableOpacity
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={
          active
            ? 'Hide player details'
            : `Players: ${playerInfo.length}, ${liveCount} online.${
                overdueName ? ` ${overdueName} (on turn), boot time passed.` : ''}${
                unreadTotal > 0 ? ` ${unreadTotal} unread secret messages.` : ''
              } Tap for details.`
        }
        testID="player-dots-strip"
        activeOpacity={0.7}
        className={`rounded-full flex-row items-center px-2 border ${
          active ? 'bg-blood/15 border-blood' : 'bg-canvas/85 border-olive'
        }`}
        // Fixed dimensions keep the capsule, rings, dots, and initials on the
        // same center line across native and web flex layouts. The on-turn
        // triangle sits inside the row's top padding so it never grows the bar.
        style={{
          width: pillWidth,
          height: 38,
          gap,
          alignSelf: alignment === 'center' ? 'center' : 'flex-end',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
      {playerInfo.map((info) => {
        const overdue = info.player.userId === overdueUserId
        const dot = playerDotState({
          isBot: info.player.isBot,
          userId: info.player.userId,
          isTurn: info.isTurn,
          eliminated: info.eliminated,
          presence: presenceSet,
        })
        const slotColor = PLAYER_COLORS[playerColorSlot(info.player)].hex
        const team = teamOf(state, String(info.player.slot))
        const ringWidth = dot.onTurn ? 2 : 1
        // A live non-turn player uses the same green for its collar and halo;
        // a dark translucent collar would read as a black seam in Chrome.
        // The current-turn player keeps the amber ring as its priority cue.
        const ringColor = dot.onTurn ? amber : dot.glow ? green : 'rgba(0,0,0,0.25)'
        // The cell is exactly the ring's footprint. The glow and the on-turn
        // triangle draw outside it inside the same SVG, so they never change
        // the pill width and stay concentric on every platform (PlayerDisc).
        return (
          <View
            key={info.player.id}
            accessibilityLabel={`${info.player.displayName}${team ? `, ${teamLabel(team)}` : ''}${info.isTurn ? ' (on turn)' : ''}${overdue ? ', boot time passed' : ''}`}
            style={{
              flexShrink: 0,
              opacity: dot.eliminated ? 0.35 : 1,
              // Keep the active player's ring and triangle above any glow
              // bleed from neighbouring live players.
              zIndex: dot.onTurn ? 3 : 1,
            }}
          >
            <PlayerDisc
              initial={info.player.displayName?.charAt(0).toUpperCase() ?? '?'}
              fill={slotColor}
              diameter={dotSize}
              fontSize={playerDotFontSize(dotSize)}
              ringWidth={ringWidth}
              ringColor={ringColor}
              glowColor={dot.glow ? green : null}
              glowBleed={GLOW_BLEED}
              marker={dot.onTurn && !overdue ? { color: amber, width: 8, height: 5, gap: 0 } : null}
              shape={team ? teamShape(team) : undefined}
            />
            {overdue && <View pointerEvents="none" style={{ position: 'absolute', top: -8, right: -6, zIndex: 5 }}><CountBubble count={1} glyph="!" testID="player-boot-alert" accessibilityLabel={`${info.player.displayName} can be booted`} /></View>}
          </View>
        )
      })}
      {unreadTotal > 0 && (
        <View pointerEvents="none" accessibilityElementsHidden style={{ position: 'absolute', top: -3, right: -3, zIndex: 5 }}>
          <CountBubble count={unreadTotal} />
        </View>
      )}
      </TouchableOpacity>
    </View>
  )
}

